import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Copy, Link2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReverifyDocs({ booking, token, onSent }) {
  const [open, setOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const request = async (email, sms) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/request-reverification`,
        { send_email: email, send_sms: sms }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
      if (email || sms) toast.success(`Re-verification request sent${email && sms ? " by email & text" : email ? " by email" : " by text"}.`);
      onSent?.();
      return data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create request"); return null; }
    finally { setBusy(false); }
  };

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied — paste it anywhere.`); }
    catch { toast.error("Could not copy to clipboard"); }
  };
  const copyLink = async () => { const d = result || await request(false, false); if (d) copy(d.url, "Link"); };
  const copyMessage = async () => { const d = result || await request(false, false); if (d) copy(d.sms_message, "Message"); };

  return (
    <>
      <button data-testid={`reverify-docs-btn-${booking.id}`} onClick={() => setOpen(true)}
        className="text-[11px] flex items-center gap-1 border border-amber-400/50 text-amber-400 px-2 py-0.5 rounded hover:bg-amber-400 hover:text-black transition-colors">
        <RefreshCw size={11} /> Re-verify Docs
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/75 backdrop-blur-sm overflow-y-auto py-10 px-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg bg-[#0C0C0C] border border-white/15 rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h3 className="text-white font-bold flex items-center gap-2"><RefreshCw size={15} className="text-amber-400" /> Request Document Re-Verification</h3>
              <button data-testid="reverify-close-btn" onClick={() => setOpen(false)} className="text-white/40 hover:text-white text-xl leading-none">×</button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-white/60 text-sm">
                Ask <span className="text-white font-semibold">{booking.name}</span> to re-submit their <b className="text-white/85">driver's license (front &amp; back)</b> and a new <b className="text-white/85">selfie</b>.
                Their current uploads will be archived, and the secure link walks them through fresh uploads.
              </p>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-white/75 cursor-pointer">
                  <input type="checkbox" data-testid="reverify-send-email" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="h-4 w-4 accent-[#F2B705]" disabled={!booking.email} />
                  Send Email {booking.email ? <span className="text-white/40 text-xs">({booking.email})</span> : <span className="text-red-400/70 text-xs">(no email on file)</span>}
                </label>
                <label className="flex items-center gap-2 text-sm text-white/75 cursor-pointer">
                  <input type="checkbox" data-testid="reverify-send-sms" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} className="h-4 w-4 accent-[#F2B705]" disabled={!booking.phone} />
                  Send Text {booking.phone ? <span className="text-white/40 text-xs">({booking.phone})</span> : <span className="text-red-400/70 text-xs">(no phone on file)</span>}
                </label>
              </div>

              <button data-testid="reverify-send-btn" onClick={() => request(sendEmail && !!booking.email, sendSms && !!booking.phone)}
                disabled={busy || (!sendEmail && !sendSms)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-[#F2B705] text-black text-sm font-bold hover:bg-white transition-colors disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Send Re-Verification Request
              </button>

              <div className="flex items-center gap-3 text-white/30 text-xs"><span className="flex-1 h-px bg-white/10" /> or share manually <span className="flex-1 h-px bg-white/10" /></div>

              <div className="grid grid-cols-2 gap-2">
                <button data-testid="reverify-copy-link" onClick={copyLink} disabled={busy}
                  className="flex items-center justify-center gap-1.5 py-2 rounded-md border border-white/15 text-white/70 text-xs font-semibold hover:border-[#F2B705]/50 hover:text-white transition-colors disabled:opacity-50">
                  <Link2 size={13} /> Copy Link
                </button>
                <button data-testid="reverify-copy-message" onClick={copyMessage} disabled={busy}
                  className="flex items-center justify-center gap-1.5 py-2 rounded-md border border-white/15 text-white/70 text-xs font-semibold hover:border-[#F2B705]/50 hover:text-white transition-colors disabled:opacity-50">
                  <Copy size={13} /> Copy Text Message
                </button>
              </div>

              {result && (
                <div className="rounded-md bg-black border border-white/10 p-3 text-xs space-y-1.5" data-testid="reverify-result">
                  {(result.sent_email || result.sent_sms) && (
                    <div className="text-green-400 font-semibold">
                      ✓ Sent{result.sent_email ? " · email" : ""}{result.sent_sms ? " · text" : ""}
                    </div>
                  )}
                  <div className="text-white/40 break-all">Secure link: <span className="text-[#F2B705]">{result.url}</span></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
